/**
 * Runs unit-06's pickers for the panel (spec section 4, Picking). The panel stays mounted but hidden
 * while picking so the draft survives. Esc is handled by the picker's own capture-phase window
 * listener, which cancels and stops the event; a cancelled pick resolves to null.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { startElementPick, startRegionPick, type PickHandle } from '@/lib/feedback';
import {
  announce,
  beginPick,
  endPick,
  getFeedbackState,
  requestFocus,
  setCapture,
} from './feedbackStore';

export type PickMode = 'element' | 'region';

export function usePickController(viewIsPicking: boolean): {
  startPick: (mode: PickMode) => void;
  pickMode: PickMode;
} {
  const [pickMode, setPickMode] = useState<PickMode>('element');
  const active = useRef<{ cancel: () => void } | null>(null);

  const track = useCallback(
    <T,>(handle: PickHandle<T>, buttonId: string, onResult: (value: T) => void): void => {
      active.current = handle;
      requestFocus('feedback-picking-banner');
      void handle.result.then((value) => {
        if (active.current === handle) active.current = null;
        const stillPicking = getFeedbackState().view === 'picking';
        if (value) onResult(value);
        // A pick cancelled because a modal paused the panel must not drag focus back.
        if (stillPicking) {
          endPick();
          requestFocus(buttonId);
        }
      });
    },
    [],
  );

  const startPick = useCallback(
    (mode: PickMode): void => {
      active.current?.cancel();
      setPickMode(mode);
      const options = { onPickStart: beginPick };
      if (mode === 'element') {
        track(startElementPick(options), 'feedback-pick-element', (element) => {
          setCapture({ mode: 'element', ...element });
          announce('Element selected.');
        });
      } else {
        track(startRegionPick(options), 'feedback-pick-region', (rect) => {
          setCapture({ mode: 'region', rect });
          announce('Region selected.');
        });
      }
    },
    [track],
  );

  // A modal (or anything else) took the panel out of the picking view: stop the pick.
  useEffect(() => {
    if (!viewIsPicking) active.current?.cancel();
  }, [viewIsPicking]);

  useEffect(
    () => () => {
      active.current?.cancel();
    },
    [],
  );

  return { startPick, pickMode };
}
